/*
 * Turrigen science story — position-driven scroll choreography.
 *
 * Every frame (rAF position-watcher; no scroll-event dependency because this
 * theme scrolls .page-wrapper on desktop and the document on mobile), each
 * scene's text panels and figure get an opacity computed from where they sit
 * in the viewport: they fade in as they enter from the bottom and fade back
 * out as they pass the top. Native scrolling only — nothing is intercepted.
 *
 * Reduced motion: the loop never starts and any inline styles are cleared —
 * CSS renders everything static and fully visible. No JS: this file never
 * runs, and the stylesheet applies no hidden states.
 */
(function () {
  var story = document.querySelector('[data-science-story]');
  if (!story) return;

  document.documentElement.classList.add('trg-story-js');

  var scenes = [].slice.call(story.querySelectorAll('[data-scene]'));
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var parts = [];

  scenes.forEach(function (scene) {
    ['.trg-scene__lead', '.trg-scene__detail', '.trg-scene__figure', '.trg-scene__widget'].forEach(function (sel) {
      var el = scene.querySelector(sel);
      if (el) parts.push({ el: el, isText: sel !== '.trg-scene__figure' && sel !== '.trg-scene__widget' });
    });
  });

  function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

  /* Lightweight behavioral signal: scene first-views and CTA clicks pushed
     to the dataLayer (picked up by GTM/pixels when configured; harmless
     no-op otherwise). No external dependencies, no PII. */
  function track(event, detail) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: event }, detail));
    } catch (e) { /* analytics must never break the page */ }
  }

  var seen = {};
  story.addEventListener('click', function (e) {
    var cta = e.target.closest('.trg-scene__cta, .trg-evidence__links a, .trg-story__skip');
    if (cta) {
      var scene = e.target.closest('[data-scene]');
      track('science_story_cta', {
        cta_label: (cta.textContent || '').trim().slice(0, 60),
        cta_href: cta.getAttribute('href'),
        scene_id: scene ? scene.id || '' : ''
      });
    }
  });

  function clearStyles() {
    parts.forEach(function (p) {
      p.el.style.opacity = '';
      p.el.style.transform = '';
    });
  }

  var running = false;
  var lastY = null;

  function frame() {
    if (reduced.matches) { running = false; clearStyles(); return; }
    var vh = window.innerHeight;
    var storyRect = story.getBoundingClientRect();

    if (storyRect.top !== lastY) {
      lastY = storyRect.top;

      /* Skip work entirely while the story is far off-screen. */
      if (storyRect.bottom > -vh && storyRect.top < vh * 2) {
        var best = null, bestDist = Infinity;

        parts.forEach(function (p) {
          var r = p.el.getBoundingClientRect();
          if (r.height === 0) return;
          /* Fade in: element top rising from 94%vh up to 74%vh. */
          var fadeIn = clamp01((vh * 0.94 - r.top) / (vh * 0.20));
          /* Fade out earlier — copy clears by the time its bottom passes 22%vh,
             so only one scene's text is ever prominent. */
          var fadeOut = clamp01((r.bottom - vh * 0.22) / (vh * 0.26));
          if (p.isText) {
            /* Only text fades; figures and widgets stay fully opaque so the
               background wave never reads through an image. */
            var o = Math.min(fadeIn, fadeOut);
            p.el.style.opacity = o.toFixed(3);
            p.el.style.transform = 'translateY(' + ((1 - fadeIn) * 26).toFixed(1) + 'px)';
          } else if (p.el.style.opacity !== '') {
            p.el.style.opacity = '';
          }
        });

        /* Nearest-to-center scene drives the wave intensity (:has() rules). */
        scenes.forEach(function (s) {
          var r = s.getBoundingClientRect();
          var d = Math.abs(r.top + r.height / 2 - vh / 2);
          if (d < bestDist) { bestDist = d; best = s; }
        });
        scenes.forEach(function (s) { s.classList.toggle('is-active', s === best); });
        if (best && best.id && !seen[best.id]) {
          seen[best.id] = true;
          track('science_scene_view', { scene_id: best.id });
        }
      }
    }
    window.requestAnimationFrame(frame);
  }

  function start() {
    if (reduced.matches) { clearStyles(); return; }
    if (!running) { running = true; lastY = null; window.requestAnimationFrame(frame); }
  }

  start();
  reduced.addEventListener && reduced.addEventListener('change', start);
  window.addEventListener('resize', function () { lastY = null; }, { passive: true });
})();
